"use client"

import { useEffect, useState, type CSSProperties, type ReactNode } from "react"
import { usePathname } from "next/navigation"
import { supabase } from "@/lib/supabaseClient"

// ONE reusable marketing-header container for every public app surface (Careers,
// GTM, Venture, ProductBrain). Everything app-specific is passed in: brand slot,
// nav items, sign-in, CTA, an optional session-aware CTA, an optional theme-toggle
// slot, and a palette. It is token-driven by default (so a themeable app like
// Careers gets light/dark for free); a dark-only app passes a `palette` override.
//
// Layout mirrors gtm.signalmax.ai: brand left, nav next, everything else pushed to
// the right with the actions cluster, and the theme toggle rendered LAST (far
// right). Collapses to a hamburger + drawer on narrow screens.

export type NavItem = { href: string; label: string }
export type HeaderAction = { href: string; label: string }
export type HeaderPalette = { bg: string; border: string; text: string; dim: string; accent: string; onAccent: string }

const TOKEN_PALETTE: HeaderPalette = {
  bg: "color-mix(in srgb, var(--canvas) 85%, transparent)",
  border: "var(--border-subtle)",
  text: "var(--text-primary)",
  dim: "var(--text-secondary)",
  // The CTA fill: the kit button fill (#E32D08 for the default orange) so the white label clears 4.5:1.
  accent: "var(--accent-fill, var(--accent))",
  onAccent: "var(--on-accent, #fff)",
}

export function MarketingHeader({
  brand,
  brandHref = "/",
  brandLabel = "Home",
  nav = [],
  signIn = null,
  cta = null,
  authedCta = null,
  search = null,
  themeToggle = null,
  maxWidth = 1240,
  palette,
}: {
  brand: ReactNode
  brandHref?: string
  brandLabel?: string
  nav?: NavItem[]
  signIn?: HeaderAction | null
  cta?: HeaderAction | null
  /** When provided, a session check swaps the CTA to this and hides Sign in for authed viewers. */
  authedCta?: HeaderAction | null
  /** Optional search control (e.g. the careers command palette). Rendered in the right cluster. */
  search?: ReactNode
  themeToggle?: ReactNode
  maxWidth?: number
  palette?: Partial<HeaderPalette>
}) {
  const pathname = usePathname() || "/"
  const [open, setOpen] = useState(false)
  const [authed, setAuthed] = useState(false)
  const P = { ...TOKEN_PALETTE, ...palette }

  // Only pay for a session check when the caller actually varies by auth.
  useEffect(() => {
    if (!authedCta) return
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (active && data.session) setAuthed(true)
    }).catch(() => {})
    return () => { active = false }
  }, [authedCta])

  const isActive = (href: string) => {
    const base = href.split("#")[0] || "/"
    if (base === "/") return pathname === "/"
    return pathname === base || pathname.startsWith(base + "/")
  }
  const effectiveCta = authed && authedCta ? authedCta : cta
  const showSignIn = signIn && !(authed && authedCta)
  const INSET = "clamp(16px, 4vw, 48px)"

  const navLink = (n: NavItem, inDrawer: boolean): CSSProperties => ({
    textDecoration: "none",
    fontSize: inDrawer ? 15 : 14.5,
    fontWeight: isActive(n.href) ? 700 : 500,
    color: isActive(n.href) ? P.text : P.dim,
    whiteSpace: "nowrap",
    ...(inDrawer
      ? { display: "block", padding: "12px 4px", borderBottom: `1px solid ${P.border}` }
      : { padding: "6px 0", borderBottom: `2px solid ${isActive(n.href) ? P.accent : "transparent"}` }),
  })
  const ctaStyle: CSSProperties = { background: P.accent, color: P.onAccent, textDecoration: "none", fontSize: 14.5, fontWeight: 700, padding: "9px 18px", borderRadius: 9, whiteSpace: "nowrap" }

  return (
    <header className="smx-mh" style={{ position: "sticky", top: 0, zIndex: 50, background: P.bg, backdropFilter: "blur(12px)", borderBottom: `1px solid ${P.border}` }}>
      <div style={{ maxWidth, margin: "0 auto", padding: `12px ${INSET}`, display: "flex", alignItems: "center", gap: "clamp(16px, 3vw, 36px)" }}>
        <a href={brandHref} aria-label={brandLabel} style={{ display: "inline-flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>{brand}</a>

        {nav.length > 0 && (
          <nav className="smx-mh-nav" style={{ display: "flex", gap: 26 }}>
            {nav.map((n) => <a key={n.href} href={n.href} style={navLink(n, false)}>{n.label}</a>)}
          </nav>
        )}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "clamp(10px, 2vw, 16px)" }}>
          {search && <div className="smx-mh-search" style={{ display: "flex", minWidth: 0 }}>{search}</div>}
          {showSignIn && <a href={signIn!.href} className="smx-mh-hide-sm" style={{ color: P.dim, textDecoration: "none", fontSize: 14.5, fontWeight: 600, whiteSpace: "nowrap" }}>{signIn!.label || "Sign in"}</a>}
          {effectiveCta && <a href={effectiveCta.href} className="smx-mh-hide-sm" style={ctaStyle}>{effectiveCta.label}</a>}
          {themeToggle}
          {(nav.length > 0 || signIn || cta) && (
            <button
              className="smx-mh-menu"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              style={{ display: "none", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: 9, background: "var(--surface, transparent)", border: `1px solid var(--border-strong, ${P.border})`, cursor: "pointer", color: P.text, padding: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {open ? <path d="M18 6 6 18M6 6l12 12" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
              </svg>
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="smx-mh-drawer" style={{ borderTop: `1px solid ${P.border}`, background: "var(--canvas, #0A1628)", padding: `8px ${INSET} 16px` }}>
          {nav.map((n) => <a key={n.href} href={n.href} onClick={() => setOpen(false)} style={navLink(n, true)}>{n.label}</a>)}
          {showSignIn && <a href={signIn!.href} onClick={() => setOpen(false)} style={{ display: "block", padding: "12px 4px", color: P.dim, fontSize: 15, fontWeight: 600, textDecoration: "none" }}>{signIn!.label || "Sign in"}</a>}
          {effectiveCta && <a href={effectiveCta.href} onClick={() => setOpen(false)} style={{ ...ctaStyle, display: "block", textAlign: "center", marginTop: 12 }}>{effectiveCta.label}</a>}
        </div>
      )}

      <style>{`
        @media (max-width: 760px){
          .smx-mh-nav{display:none !important}
          .smx-mh-hide-sm{display:none !important}
          .smx-mh-menu{display:inline-flex !important}
        }
        @media (min-width: 761px){
          .smx-mh-drawer{display:none !important}
        }
      `}</style>
    </header>
  )
}
